import { useMemo } from "react";

/**
 * Renders a subtle randomized topographic contour SVG that fills its parent.
 * Uses procedural closed curves so it never looks like a repeating tile.
 * Memoized per mount so the pattern is stable but not identical between mounts.
 */
export const TopoBackground = ({ seed = 1 }) => {
  const paths = useMemo(() => buildContours(seed), [seed]);
  return (
    <svg
      data-testid="topo-bg-svg"
      className="absolute inset-0 w-full h-full pointer-events-none"
      viewBox="0 0 1000 1000"
      preserveAspectRatio="xMidYMid slice"
      aria-hidden="true"
    >
      <defs>
        <radialGradient id="topoGlow" cx="30%" cy="35%" r="70%">
          <stop offset="0%" stopColor="rgba(239,68,98,0.05)" />
          <stop offset="100%" stopColor="rgba(0,0,0,0)" />
        </radialGradient>
        <radialGradient id="topoGlow2" cx="80%" cy="80%" r="60%">
          <stop offset="0%" stopColor="rgba(255,107,61,0.035)" />
          <stop offset="100%" stopColor="rgba(0,0,0,0)" />
        </radialGradient>
      </defs>
      <rect x="0" y="0" width="1000" height="1000" fill="#0d0e13" />
      <rect x="0" y="0" width="1000" height="1000" fill="url(#topoGlow)" />
      <rect x="0" y="0" width="1000" height="1000" fill="url(#topoGlow2)" />
      {paths.map((p, i) => (
        <path
          key={i}
          d={p.d}
          fill="none"
          stroke={p.stroke}
          strokeWidth={p.width}
          strokeLinejoin="round"
          strokeLinecap="round"
          opacity={p.opacity}
        />
      ))}
    </svg>
  );
};

// Small deterministic pseudo-random (mulberry32) so builds are stable.
function mulberry32(a) {
  return function () {
    let t = (a += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function buildContours(seed) {
  const rng = mulberry32(seed * 9301 + 49297);
  const paths = [];
  // Several "peaks" — each is a set of nested organic contour rings.
  const peaks = 5;
  for (let p = 0; p < peaks; p++) {
    const cx = rng() * 900 + 50;
    const cy = rng() * 900 + 50;
    const rings = 6 + Math.floor(rng() * 5);
    const baseRadius = 40 + rng() * 90;
    const wobble = 0.15 + rng() * 0.35;
    const rotation = rng() * Math.PI * 2;
    for (let r = 0; r < rings; r++) {
      const radius = baseRadius + r * (14 + rng() * 6);
      const d = organicClosedPath(cx, cy, radius, wobble, rotation + r * 0.13, rng);
      paths.push({
        d,
        stroke: r % 3 === 0 ? "#232833" : "#1c2028",
        width: r % 3 === 0 ? 1.1 : 0.7,
        opacity: 0.55 - r * 0.03,
      });
    }
  }
  // Scattered small contour rings for texture
  const smalls = 22;
  for (let i = 0; i < smalls; i++) {
    const cx = rng() * 1000;
    const cy = rng() * 1000;
    const rad = 8 + rng() * 22;
    const d = organicClosedPath(cx, cy, rad, 0.3 + rng() * 0.3, rng() * Math.PI, rng);
    paths.push({
      d,
      stroke: "#1a1d24",
      width: 0.6,
      opacity: 0.4,
    });
  }
  return paths;
}

function organicClosedPath(cx, cy, radius, wobble, rotationOffset, rng) {
  const steps = 24 + Math.floor(rng() * 8);
  // Precompute per-vertex jitter so the closed loop stays coherent
  const jitters = [];
  for (let i = 0; i < steps; i++) {
    jitters.push(1 + (rng() * 2 - 1) * wobble);
  }
  const pts = [];
  for (let i = 0; i < steps; i++) {
    const a = (i / steps) * Math.PI * 2 + rotationOffset;
    const rr = radius * jitters[i];
    pts.push([cx + Math.cos(a) * rr, cy + Math.sin(a) * rr]);
  }
  // Build a smooth closed spline through pts via cubic Béziers.
  let d = `M ${pts[0][0].toFixed(1)} ${pts[0][1].toFixed(1)}`;
  const n = pts.length;
  const tension = 0.35;
  for (let i = 0; i < n; i++) {
    const p0 = pts[(i - 1 + n) % n];
    const p1 = pts[i];
    const p2 = pts[(i + 1) % n];
    const p3 = pts[(i + 2) % n];
    const c1x = p1[0] + (p2[0] - p0[0]) * tension;
    const c1y = p1[1] + (p2[1] - p0[1]) * tension;
    const c2x = p2[0] - (p3[0] - p1[0]) * tension;
    const c2y = p2[1] - (p3[1] - p1[1]) * tension;
    d += ` C ${c1x.toFixed(1)} ${c1y.toFixed(1)}, ${c2x.toFixed(1)} ${c2y.toFixed(1)}, ${p2[0].toFixed(1)} ${p2[1].toFixed(1)}`;
  }
  d += " Z";
  return d;
}
